"use client";

import { BrandHeaderLogo } from "@/components/BrandHeaderLogo";
import { cn } from "@/lib/utils";
import { UserButton } from "@clerk/nextjs";
import { api } from "convex/_generated/api";
import { useConvexAuth, useQuery } from "convex/react";
import {
  ClipboardCheck,
  Library,
  CreditCard,
  Infinity,
  LayoutDashboard,
  Layers,
  Mic,
  Moon,
  Settings,
  ShieldCheck,
  Sun,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";

const WORKSPACE_ITEMS = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/tests/new", label: "Tests", icon: ClipboardCheck, isNew: true },
  { href: "/audio/new", label: "Audio", icon: Mic, isNew: true },
  { href: "/templates", label: "Templates", icon: Layers },
  { href: "/history", label: "My resources", icon: Library },
] as const;

const ACCOUNT_ITEMS = [
  { href: "/buy", label: "Plan & billing", icon: CreditCard },
  { href: "/settings", label: "Settings", icon: Settings },
] as const;

const MOBILE_NAV = WORKSPACE_ITEMS;

export function AppSidebar() {
  const pathname = usePathname() ?? "";
  const { isAuthenticated } = useConvexAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const isAdmin = useQuery(
    api.admin.isAdmin,
    isAuthenticated ? undefined : "skip",
  );

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  const isDark = resolvedTheme === "dark";
  const toggleTheme = () => setTheme(isDark ? "light" : "dark");

  return (
    <>
      {/* ── Desktop sidebar ──────────────────────────────────── */}
      <aside
        className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-[15.5rem] lg:z-30"
        style={{
          background: "var(--sidebar-bg)",
          borderRight: "1px solid var(--sidebar-border)",
        }}
      >
        {/* Logo */}
        <div
          className="flex h-[4.25rem] shrink-0 items-center px-5"
          style={{ borderBottom: "1px solid var(--sidebar-border)" }}
        >
          <Link
            href="/dashboard"
            aria-label="darsi.ma — dashboard"
            className="inline-flex items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            <BrandHeaderLogo />
          </Link>
        </div>

        {/* Nav items */}
        <nav
          aria-label="Primary navigation"
          className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-0.5"
        >
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Workspace
          </p>
          {WORKSPACE_ITEMS.map(({ href, label, icon: Icon, ...rest }) => {
            const isNew = "isNew" in rest ? rest.isNew : false;
            const active = isActive(href);
            return (
              <Link
                key={href}
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150",
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Icon
                  className={cn(
                    "h-[1.05rem] w-[1.05rem] shrink-0 transition-colors duration-150",
                    active ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground",
                  )}
                />
                <span className="flex-1">{label}</span>
                {isNew && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide",
                      active
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-accent text-accent-foreground",
                    )}
                  >
                    New
                  </span>
                )}
              </Link>
            );
          })}

          <p className="mb-2 mt-5 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Account
          </p>
          {ACCOUNT_ITEMS.map(({ href, label, icon: Icon }) => {
            const active = isActive(href);
            return (
              <Link
                key={href}
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150",
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Icon className="h-[1.05rem] w-[1.05rem] shrink-0" />
                <span className="flex-1">{label}</span>
              </Link>
            );
          })}

          {isAdmin && (
            <Link
              href="/admin"
              aria-current={isActive("/admin") ? "page" : undefined}
              className={cn(
                "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150",
                isActive("/admin")
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <ShieldCheck className="h-[1.05rem] w-[1.05rem] shrink-0" />
              <span>Admin</span>
            </Link>
          )}
        </nav>

        {/* Subscription status + user + theme toggle */}
        <div
          className="shrink-0 flex flex-col gap-3 px-3 py-4"
          style={{ borderTop: "1px solid var(--sidebar-border)" }}
        >
          <SidebarPlanStatus />
          <div className="flex items-center gap-2 px-1">
            <UserButton
              appearance={{
                elements: { userButtonAvatarBox: "h-8 w-8" },
              }}
            />
            <p className="flex-1 text-xs text-muted-foreground">My account</p>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </aside>

      {/* ── Mobile top bar ───────────────────────────────────── */}
      <header
        className="lg:hidden sticky top-0 z-30 flex h-14 items-center justify-between gap-2 px-4"
        style={{
          background: "var(--sidebar-bg)",
          borderBottom: "1px solid var(--sidebar-border)",
        }}
      >
        <Link
          href="/dashboard"
          aria-label="darsi.ma — dashboard"
          className="inline-flex items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <BrandHeaderLogo className="h-6 w-auto" />
        </Link>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Mobile plan badge */}
        <MobilePlanBadge />

        {/* Avatar */}
        <UserButton
          appearance={{
            elements: { userButtonAvatarBox: "h-8 w-8" },
          }}
        />
      </header>

      {/* ── Mobile bottom nav ────────────────────────────────── */}
      <nav
        aria-label="Mobile navigation"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 flex items-stretch"
        style={{
          background: "var(--sidebar-bg)",
          borderTop: "1px solid var(--sidebar-border)",
        }}
      >
        {MOBILE_NAV.map(({ href, label, icon: Icon }) => {
          const active = isActive(href);
          return (
            <Link
              key={href}
              href={href}
              aria-current={active ? "page" : undefined}
              className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors duration-150"
            >
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-xl transition-all duration-150",
                  active ? "bg-primary" : "bg-transparent",
                )}
              >
                <Icon
                  className={cn(
                    "h-4 w-4",
                    active ? "text-primary-foreground" : "text-muted-foreground",
                  )}
                />
              </span>
              <span className={active ? "text-foreground" : "text-muted-foreground"}>
                {label}
              </span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}

function MobilePlanBadge() {
  const { isAuthenticated } = useConvexAuth();
  const creditInfo = useQuery(
    api.creditRequests.getMine,
    isAuthenticated ? undefined : "skip",
  );

  if (!creditInfo?.billing) return null;

  const billing = creditInfo.billing;
  const activePlan = billing.subscriptionPlan;
  const isExpiring = billing.billingUiState.kind === "subscription_expiring";
  const label = activePlan
    ? isExpiring ? "Renew" : activePlan === "pro" ? "Pro" : "Starter"
    : billing.trialEligible
      ? `Trial · ${billing.trialDaysRemaining ?? 0}d`
      : billing.credits > 0
        ? `${billing.credits} lessons`
        : "Plans";
  return <Link href={isExpiring ? "/buy?section=overview&entry=near_expiration" : "/buy"} aria-label={`Plan and billing: ${label}`} className="inline-flex min-h-8 items-center gap-1 rounded-lg bg-primary/10 px-2 text-[10px] font-semibold text-primary"><CreditCard className="size-3" />{label}</Link>;
}

function SidebarPlanStatus() {
  const campaign = useQuery(api.backToSchoolCampaign.getPublicCampaignState);
  const campaignActive = campaign?.status === "active";
  const { isAuthenticated } = useConvexAuth();
  const creditInfo = useQuery(
    api.creditRequests.getMine,
    isAuthenticated ? undefined : "skip",
  );

  if (!creditInfo) return null;

  const billing = creditInfo.billing;
  const pending = creditInfo.requests.find((request) => request.status === "pending");
  const activePlan = billing.subscriptionPlan;
  const expiredPlan = !activePlan ? billing.lastSubscriptionPlan : null;
  const expires = billing.subscriptionExpiresAt;
  const date = expires ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(expires)) : null;
  const isExpiring = billing.billingUiState.kind === "subscription_expiring";
  const title = pending
    ? "Payment under review"
    : activePlan
      ? `${activePlan === "pro" ? "Pro" : "Starter"}${date ? ` · ${date}` : ""}`
      : billing.trialEligible
        ? `Pro trial · ${billing.trialDaysRemaining ?? 0} days left`
        : expiredPlan
          ? `${expiredPlan === "pro" ? "Pro" : "Starter"} expired`
          : billing.credits > 0
            ? `${billing.credits} lesson credits`
            : "Free plan";
  const description = pending
    ? "Track manual verification."
    : activePlan === "pro"
      ? "Lessons, Tests, Audio & Templates."
      : activePlan === "starter"
        ? "Unlimited lesson plans."
        : billing.trialEligible
          ? "Explore all Darsi features."
          : billing.credits > 0
            ? "Credits do not expire."
            : "Unlock Pro teaching tools.";
  const action = pending ? "View payment" : campaignActive ? "Get 50% off" : isExpiring ? `Renew ${activePlan === "pro" ? "Pro" : "Starter"}` : activePlan ? "Manage" : billing.credits > 0 ? "Buy credits" : "View plans";
  const href = pending ? "/buy?section=payments" : campaignActive ? "/buy?section=plans&promo=SCHOOL26&entry=sidebar_campaign" : isExpiring ? "/buy?section=overview&entry=near_expiration" : billing.credits > 0 && !activePlan ? "/buy?section=credits" : "/buy";
  return <Link href={href} className={cn("block rounded-xl border p-3 transition-colors hover:bg-muted", isExpiring ? "border-amber-300 bg-amber-50/50" : "border-border bg-card")}><div className="flex items-start gap-2.5"><span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10"><Infinity className="size-3.5 text-primary" /></span><div className="min-w-0">{campaignActive && !pending ? <span className="mb-1 inline-flex rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-800">Back to School · 50% off</span> : null}<p className="text-xs font-semibold leading-tight text-foreground">{title}</p><p className="mt-1 text-[10px] leading-4 text-muted-foreground">{description}</p><p className="mt-2 text-[10px] font-semibold text-primary">{action} →</p></div></div></Link>;
}
